import React, { useEffect, useState } from 'react';
import {
  MdCheckCircle,
  MdError,
  MdRefresh,
  MdRestartAlt,
} from 'react-icons/md';

interface BookingSubmissionStatusProps {
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitError: string | null;
  onTryAgain: () => void;
  onStartOver: () => void;
  children?: React.ReactNode;
}

export const BookingSubmissionStatus: React.FC<
  BookingSubmissionStatusProps
> = ({
  isSubmitting,
  isSubmitted,
  submitError,
  onTryAgain,
  onStartOver,
  children,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState([false, false, false]);

  useEffect(() => {
    if (isSubmitting) {
      // Reset progress when starting
      setCurrentStep(0);
      setStepProgress([false, false, false]);

      // Animate through steps
      const stepTimers = [
        setTimeout(() => {
          setCurrentStep(1);
          setStepProgress([true, false, false]);
        }, 1500),
        setTimeout(() => {
          setCurrentStep(2);
          setStepProgress([true, true, false]);
        }, 3000),
        setTimeout(() => {
          setCurrentStep(3);
          setStepProgress([true, true, true]);
        }, 4500),
      ];

      return () => {
        stepTimers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [isSubmitting]);
  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        {/* Progress Indicator */}
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Processing your booking...
          </h3>
          <p className="text-sm text-gray-600 max-w-md">
            Please wait while we confirm your reservation and generate your
            ticket.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-500 ${
                stepProgress[0]
                  ? 'bg-green-500 scale-125'
                  : currentStep === 0
                    ? 'bg-blue-600 animate-pulse'
                    : 'bg-gray-300'
              }`}
            ></div>
            <span
              className={`text-xs transition-colors duration-500 ${
                stepProgress[0]
                  ? 'text-green-600 font-medium'
                  : currentStep === 0
                    ? 'text-blue-600'
                    : 'text-gray-400'
              }`}
            >
              Validating payment
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-500 ${
                stepProgress[1]
                  ? 'bg-green-500 scale-125'
                  : currentStep === 1
                    ? 'bg-blue-600 animate-pulse'
                    : 'bg-gray-300'
              }`}
            ></div>
            <span
              className={`text-xs transition-colors duration-500 ${
                stepProgress[1]
                  ? 'text-green-600 font-medium'
                  : currentStep === 1
                    ? 'text-blue-600'
                    : 'text-gray-400'
              }`}
            >
              Creating ticket
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-500 ${
                stepProgress[2]
                  ? 'bg-green-500 scale-125'
                  : currentStep === 2
                    ? 'bg-blue-600 animate-pulse'
                    : 'bg-gray-300'
              }`}
            ></div>
            <span
              className={`text-xs transition-colors duration-500 ${
                stepProgress[2]
                  ? 'text-green-600 font-medium'
                  : currentStep === 2
                    ? 'text-blue-600'
                    : 'text-gray-400'
              }`}
            >
              Sending confirmation
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="space-y-6">
        {/* Success Message */}
        <div className="flex items-center justify-center py-4">
          <div className="flex items-center space-x-3 text-green-600">
            <MdCheckCircle className="w-8 h-8" />
            <span className="text-lg font-semibold">
              Booking submitted successfully!
            </span>
          </div>
        </div>

        {/* Confirmation Content */}
        {children}
      </div>
    );
  }

  if (submitError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        {/* Error Icon */}
        <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
          <MdError className="w-8 h-8 text-red-600" />
        </div>

        {/* Error Message */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Booking submission failed
          </h3>
          <p className="text-sm text-gray-600 max-w-md text-red-700 whitespace-pre-wrap">
            {submitError}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onTryAgain}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 shadow-sm"
          >
            <MdRefresh className="w-4 h-4 mr-2" />
            Try Again
          </button>

          <button
            onClick={onStartOver}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 shadow-sm"
          >
            <MdRestartAlt className="w-4 h-4 mr-2" />
            Start Over
          </button>
        </div>

        {/* Additional Help */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            If the problem persists, please contact our support team.
          </p>
        </div>
      </div>
    );
  }

  return null;
};
